import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
  SvgIcon,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  trigger: {
    color: theme.palette.type === "dark" ? "#b7c5ce" : "#53636d",
  },
  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 8,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: 3,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
    gap: 12,
    minWidth: 560,
    padding: "8px 2px 16px",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  sticker: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    background: theme.palette.type === "dark" ? "#17242d" : "linear-gradient(145deg, #f8fcfe, #e8f4f9)",
    padding: 8,
    cursor: "pointer",
    transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
    position: "relative",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 10px 26px rgba(2, 99, 155, .18)",
      borderColor: "#43a9d3",
    },
    "&:disabled": { cursor: "wait", opacity: .65 },
  },
  image: { width: "100%", aspectRatio: "1 / 1", objectFit: "contain", display: "block" },
  label: { display: "block", textAlign: "center", fontWeight: 700, color: theme.palette.text.primary, marginTop: 3 },
  sending: { position: "absolute", inset: 0, display: "grid", placeItems: "center", borderRadius: 16, background: "rgba(255,255,255,.7)" },
}));

// Pegatina con esquina doblada y rostro: se distingue del selector de emojis.
const StickerIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      d="M6 2.75h10A3.25 3.25 0 0 1 19.25 6v8.35L13.6 21.25H6A3.25 3.25 0 0 1 2.75 18V6A3.25 3.25 0 0 1 6 2.75Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M13.6 21.25v-3.1a3.8 3.8 0 0 1 3.8-3.8h1.85"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="9" r="1" fill="currentColor" />
    <circle cx="14" cy="9" r="1" fill="currentColor" />
    <path d="M7.8 12.7c.9 1.2 2 1.8 3.3 1.8s2.4-.6 3.3-1.8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </SvgIcon>
);

const StickerPicker = ({ ticketId, disabled = false }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState("");
  const [pack, setPack] = useState({ name: "Stickers Facilweb", stickers: [] });

  useEffect(() => {
    if (!open || pack.stickers.length) return;
    setLoading(true);
    api.get("/stickers/facilweb")
      .then(({ data }) => setPack(data))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [open, pack.stickers.length]);

  const sendSticker = async (sticker) => {
    if (sending) return;
    setSending(sticker.file);
    try {
      await api.post(`/stickers/facilweb/${encodeURIComponent(sticker.file)}/send/${ticketId}`);
      toast.success("Sticker enviado");
      setOpen(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSending("");
    }
  };

  return (
    <>
      <Tooltip title="Stickers Facilweb">
        <span>
          <IconButton aria-label="Stickers Facilweb" disabled={disabled} onClick={() => setOpen(true)}>
            <StickerIcon className={classes.trigger} />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={() => !sending && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle disableTypography className={classes.title}>
          <Box>
            <Typography variant="h6">{pack.name}</Typography>
            <Typography variant="body2" className={classes.subtitle}>Seleccione un sticker para enviarlo inmediatamente.</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} disabled={Boolean(sending)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? <Box py={6} textAlign="center"><CircularProgress /></Box> : (
            <div className={classes.grid}>
              {pack.stickers.map(sticker => (
                <button key={sticker.file} type="button" className={classes.sticker} onClick={() => sendSticker(sticker)} disabled={Boolean(sending)}>
                  <img src={sticker.url} alt={sticker.label} className={classes.image} />
                  <span className={classes.label}>{sticker.label}</span>
                  {sending === sticker.file && <span className={classes.sending}><CircularProgress size={30} /></span>}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StickerPicker;
